import type { PlatformRuntimeBootstrap } from '@crest/platform-contracts/runtime';
import {
  PlatformAccountPasswordFailure, PlatformAccountPasswordPanel,
  type PlatformAccountPasswordAction,
} from '@crest/platform-web/account';
import { usePlatformBootstrap } from '@crest/platform-web/runtime';
import { useCallback, useEffect, useMemo } from 'react';
import { CrestAccountAccessPanel, type CrestAccountAccessSummary } from './crest-account-access.panel.js';
import { CrestAuthFailure } from './crest-auth-client.js';
import { useCrestAuth, type CrestAuthStatus } from './crest-auth-provider.js';

type AccountBootstrap = Pick<PlatformRuntimeBootstrap['data'],
  'tenant' | 'actor' | 'membership' | 'revisions'>;

export interface CrestAccountPasswordContext {
  readonly tenantId: string;
  readonly userId: string;
  readonly scopeKey: string;
}

export function buildCrestAccountPasswordContext(
  bootstrap: AccountBootstrap,
  auth: Readonly<{ status: CrestAuthStatus; tenantId: string; sessionGeneration: number }>,
): CrestAccountPasswordContext | null {
  if (auth.status !== 'authenticated' || bootstrap.actor.type !== 'user' ||
      bootstrap.tenant.id !== auth.tenantId || !Number.isSafeInteger(auth.sessionGeneration) ||
      auth.sessionGeneration <= 0) return null;
  // Bootstrap has already passed the public Platform schema. Its revisions bind
  // this view to the tenant, member and current delegated-access interpretation.
  return {
    tenantId: bootstrap.tenant.id,
    userId: bootstrap.actor.userId,
    scopeKey: JSON.stringify([
      bootstrap.tenant.id, bootstrap.actor.userId, bootstrap.membership.id,
      bootstrap.revisions.membership, bootstrap.revisions.permissions, bootstrap.revisions.session,
      bootstrap.revisions.entitlements, bootstrap.revisions.configuration, bootstrap.revisions.access,
      auth.sessionGeneration,
    ]),
  };
}

/** Used by the composition, including scope changes that later return to the same key. */
export class CrestAccountPasswordViewFence {
  private active = false;
  private scopeKey: string | null = null;
  private generation = 0;

  public setContext(scopeKey: string | null): void {
    if (this.scopeKey !== scopeKey) {
      this.scopeKey = scopeKey;
      this.generation += 1;
    }
  }

  public activate(): void { this.active = true; this.generation += 1; }
  public deactivate(): void { this.active = false; this.generation += 1; }

  public capture(scopeKey: string, childIsCurrent: () => boolean): () => boolean {
    const generation = this.generation;
    return () => {
      try {
        return this.active && this.generation === generation &&
          this.scopeKey === scopeKey && childIsCurrent();
      } catch {
        return false;
      }
    };
  }
}

export function crestAccountPasswordUiFailure(error: unknown): PlatformAccountPasswordFailure {
  if (error instanceof CrestAuthFailure) {
    if (error.stage === 'password-change-session-changed' || error.stage === 'session-changed' ||
        error.stage === 'session-missing') return new PlatformAccountPasswordFailure('session-changed');
    if (error.stage === 'password-change-reauthentication' && (error.status === 400 || error.status === 401)) {
      return new PlatformAccountPasswordFailure('current-password-rejected');
    }
    if (error.stage === 'password-change-rejected') {
      if (error.status === 422) return new PlatformAccountPasswordFailure('password-policy');
      if (error.status === 400 || error.status === 401 || error.status === 403) {
        return new PlatformAccountPasswordFailure('reauthentication-required');
      }
    }
  }
  return new PlatformAccountPasswordFailure('unavailable');
}

export function CrestAccountPasswordPageView(props: {
  readonly context: CrestAccountPasswordContext | null;
  readonly changePassword: PlatformAccountPasswordAction;
  readonly accessSummary?: CrestAccountAccessSummary;
}) {
  if (props.context === null) {
    return <p role="alert">Your signed-in account context is not available. Sign in again to continue.</p>;
  }
  return (
    <>
      <PlatformAccountPasswordPanel key={props.context.scopeKey} scopeKey={props.context.scopeKey}
        changePassword={props.changePassword} />
      {props.accessSummary === undefined ? null : <CrestAccountAccessPanel {...props.accessSummary} />}
    </>
  );
}

export function CrestAccountPasswordPage(props: { readonly accessSummary?: CrestAccountAccessSummary }) {
  const auth = useCrestAuth();
  const bootstrap = usePlatformBootstrap();
  const context = buildCrestAccountPasswordContext(bootstrap, auth);
  const fence = useMemo(() => new CrestAccountPasswordViewFence(), []);
  // Invalidate at render time: a changed actor/tenant must not wait for a passive effect.
  fence.setContext(context?.scopeKey ?? null);
  useEffect(() => {
    fence.activate();
    return () => fence.deactivate();
  }, [fence]);

  const changePassword = useCallback<PlatformAccountPasswordAction>(async (input, childIsCurrent) => {
    if (context === null) throw new PlatformAccountPasswordFailure('session-changed');
    const isCurrent = fence.capture(context.scopeKey, childIsCurrent);
    if (!isCurrent()) throw new PlatformAccountPasswordFailure('session-changed');
    try {
      await auth.changeSignedInPassword(input, {
        tenantId: context.tenantId, userId: context.userId, isCurrent,
      });
    } catch (error) {
      throw crestAccountPasswordUiFailure(error);
    }
  }, [auth.changeSignedInPassword, context, fence]);

  return <CrestAccountPasswordPageView context={context} changePassword={changePassword}
    {...(props.accessSummary === undefined ? {} : { accessSummary: props.accessSummary })} />;
}


